import { SimpleFooterLayout } from '@user/layouts/SimpleFooterLayout';
import BalusLogo from '@assets/balus-logo-brand-horizontal.svg';
import { useNavigate, useLocation } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatButton } from '@framework/ui/atoms';
import { EditPasswordForm } from '@user/EditPasswordForm';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { NotFound } from '@framework/ui/pages/NotFound';
import { trpcClient } from '@framework/trpcClient';

export const SignUpRequestConfirmPage: React.FC<unknown> = () => {
  const { t } = useTranslation();
  const [signUpRequestToken, setSignUpRequestToken] = useState('');
  const [name, setName] = useState('');
  const [mailAddress, setMailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const paramToken = useMemo(() => new URLSearchParams(location.search).get('token'), [location]);

  const handleChangePassword = useCallback((password: string) => {
    setPassword(password);
  }, []);
  const isEmpty = password === '';

  const handleSubmit = useCallback(async () => {
    setProcessing(true);

    try {
      const response = await trpcClient.user.auth.signUpPasswordAuth.mutate({
        name,
        mailAddress,
        password,
        signUpMode: 'withoutInvitation',
        signUpRequestToken,
      });
      if (response.ok) {
        await FirebaseAuthenticationClient.signInWithCustomToken(response.token);
        navigate('/');
      } else {
        if (response.message === 'This email address is already registered.') {
          setError(t('auth.signUpConfirm.emailAlreadyRegistered'));
        } else {
          setError(t('auth.signUpConfirm.submitError'));
        }
      }
    } catch (error) {
      console.error(error);
      setError(t('auth.signUpConfirm.submitError'));
    } finally {
      setProcessing(false);
    }
  }, [navigate, mailAddress, name, password, signUpRequestToken, t]);

  useEffect(() => {
    if (!paramToken) {
      setSignUpRequestToken('invalid');
      return;
    }

    trpcClient.user.auth.fetchSignUpRequestToken
      .query({ signUpRequestToken: paramToken })
      .then((data) => {
        if (data.status === 'ok') {
          setName(data.name);
          setMailAddress(data.mailAddress);
          setSignUpRequestToken(paramToken);
          return;
        }

        if (data.status === 'invalid' || data.status === 'notFound') {
          setSignUpRequestToken('invalid');
          return;
        }
      })
      .catch((error) => {
        console.error(error);
        setError(t('auth.signUpConfirm.fetchError'));
      });
  }, [paramToken, t]);

  if (signUpRequestToken === 'invalid') {
    return <NotFound message={t('auth.signUpConfirm.invalidToken')} />;
  }

  return (
    <SimpleFooterLayout>
      <div className="flex w-screen flex-col items-center p-8">
        <img src={BalusLogo} className="w-64" />

        <div className="py-8 text-3xl font-bold">{t('auth.signUpConfirm.title')}</div>
        <hr className="w-full" />

        {/* content */}
        <div className="flex flex-col p-8" style={{ width: '450px' }}>
          <div className="pb-2 font-bold">{t('auth.signUpConfirm.emailLabel')}</div>
          <input
            type="text"
            className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
            defaultValue={mailAddress}
            disabled
          />

          <div className="pb-2 pt-8 font-bold">{t('auth.signUpConfirm.nameLabel')}</div>
          <input type="text" className="rounded-sm border p-2" defaultValue={name} disabled />

          <EditPasswordForm onChange={handleChangePassword} disabled={processing} />

          {error && <div className="pt-8 text-red-700">{error}</div>}
        </div>

        {/* footer */}
        <hr className="w-full" />
        <div className="flex justify-end px-8 pb-8 pt-4">
          <FlatButton
            color="brand"
            onClick={handleSubmit}
            className="ml-2 px-8"
            disabled={isEmpty || processing}
            loading={processing}
          >
            {t('auth.signUpConfirm.submitButton')}
          </FlatButton>
        </div>
      </div>
    </SimpleFooterLayout>
  );
};
